import { Alert } from "@mui/lab";
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItem, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { withStyles, WithStyles } from "@mui/styles";
import { DrawerBoxStyleClasses } from "assets/jss/common-styles";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import IconButtonClose from "components/Buttons/IconButtonClose";
import IconButtonDelete from "components/Buttons/IconButtonDelete";
import { JoinComponents } from "components/JoinComponents";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiPagination from "components/MuiExtras/MuiPagination";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import { GetSetupKnowledgeTestExceptionDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import SetupKnowledgeTestExceptionEditDialog from "views/System/Components/SetupKnowledgeTestExceptionEditDialog";

/////////////////////////////////////////////////////////////////////////////////////

const MaxTakeRecords = 10;

interface IProps {
    exceptions: GetSetupKnowledgeTestExceptionDto[];
    onClose: (data: GetSetupKnowledgeTestExceptionDto[]) => void;
}

interface IState {
    dialog: any;
    data: GetSetupKnowledgeTestExceptionDto[];
    offset: number;
}

type Props = IProps & WithTranslation & WithSnackbar & WithStyles<typeof DrawerBoxStyleClasses>;

/////////////////////////////////////////////////////////////////////////////////////

class SetupKnowledgeTestExceptionDialog extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            dialog: null,
            data: props.exceptions,
            offset: 0,
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.System.Components.SetupKnowledgeTestExceptionDialog." + r, f);
    }

    ///
    private handleClose = () => {
        this.props.onClose(this.state.data);
    };

    ///
    private handleAdd() {
        this.setState({
            dialog: (
                <SetupKnowledgeTestExceptionEditDialog
                    checkTopicExists={(topicId) => this.props.exceptions.findIndex(p => p.topicId == topicId) > -1}
                    record={null}
                    onClose={
                        (record) => {
                            if (record != null) {
                                this.setState((state) => {
                                    var newData = state.data.concat([record]);
                                    return { data: newData, dialog: null };
                                });
                            }
                            else
                                this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    }

    ///
    private handleEdit(record: GetSetupKnowledgeTestExceptionDto) {
        this.setState({
            dialog: (
                <SetupKnowledgeTestExceptionEditDialog
                    checkTopicExists={(topicId) => this.props.exceptions.findIndex(p => p.topicId == topicId) > -1}
                    record={record}
                    onDelete={(item) => this.handleDelete(item)}
                    onClose={
                        (record) => {
                            if (record != null) {
                                this.setState((state) => {
                                    var f = state.data.filter(p => p.topicId != record.topicId);
                                    var newData = f.concat([record]);
                                    return { data: newData, dialog: null };
                                });
                            }
                            else
                                this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    }

    ///
    private handleDelete(item: GetSetupKnowledgeTestExceptionDto) {
        this.setState({
            dialog: (
                <MuiConfirmation
                    message={this.res("ConfirmationDeleteMessage")}
                    onClose={
                        (result) => {
                            if (result) {
                                this.setState({
                                    data: this.state.data.filter(p => p.topicId != item.topicId),
                                    dialog: null
                                });
                            }
                            else {
                                this.setState({ dialog: null });
                            }
                        }
                    }
                />
            )
        });
    }

    ///
    private renderList() {
        const max = this.state.offset + MaxTakeRecords;
        const data = this.state.data.sort((a, b) => a.topicName.localeCompare(b.topicName));
        const list = data.filter((item, index) => index >= this.state.offset && index < max);

        var l = list.map((item) => {
            var subtitle = [];
            subtitle.push(<span>{this.res("SubtitleL1", { count: item.questionsBySession })}</span>);
            if (item.showCorrectAnswer) subtitle.push(<span>{this.res("SubtitleShowCorrectLabel")}</span>);
            else subtitle.push(<span>{this.res("SubtitleNotShowCorrectLabel")}</span>);

            return (
                <React.Fragment key={item.topicId}>
                    <ListItem>
                        <Grid container spacing={1} alignItems="center" >
                            <Grid item xs>
                                <MuiLink color="secondary" onClick={() => this.handleEdit(item)}>{item.topicName}</MuiLink>
                                <div>
                                    <small>
                                        <JoinComponents components={subtitle} separator="&ndash;" />
                                    </small>
                                </div>
                            </Grid>
                            <Grid item>
                                <IconButtonDelete size="small" color="error" onClick={() => this.handleDelete(item)} />
                            </Grid>
                        </Grid>
                    </ListItem>
                    <Divider />
                </React.Fragment>
            );
        });

        return <List>{l}</List>;
    }

    ///
    private renderPager() {
        const { exceptions } = this.props;

        if (exceptions == null || exceptions.length == 0)
            return null;

        return (
            <MuiPagination
                limit={MaxTakeRecords}
                offset={this.state.offset}
                total={exceptions.length}
                onClick={(e, offset) => this.setState({ offset })}
            />
        );
    }

    ///
    public render() {
        const { classes } = this.props;

        return (
            <>
                {this.state.dialog}

                <Dialog
                    open
                    fullWidth
                    maxWidth="md"
                    onBackdropClick={() => this.handleClose()}
                    onClose={() => this.handleClose()}
                >
                    <DialogTitle>
                        <Grid container wrap="nowrap" alignItems="center">
                            <Grid item>
                                <IconButtonAdd color="warning" onClick={() => this.handleAdd()} />
                            </Grid>
                            <Grid item xs zeroMinWidth>
                                <Typography noWrap={true} variant="h6" className={classes.title}>{this.res("BoxTitle")}</Typography>
                            </Grid>
                            <Grid item>
                                <IconButtonClose color="black" onClick={() => this.handleClose()} />
                            </Grid>
                        </Grid>
                    </DialogTitle>

                    <DialogContent>
                        {
                            this.state.data.length == 0
                                ? <Alert severity="info">{this.res("EmptyData")}</Alert>
                                : this.renderList()
                        }
                    </DialogContent>

                    <DialogActions >
                        {this.renderPager()}
                    </DialogActions>
                </Dialog >
            </>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(DrawerBoxStyleClasses)(SetupKnowledgeTestExceptionDialog)));